import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ManualFeatureType } from '@/config/manualCadFeatures';
import type {
  ManualFeatureInput,
  ManualPlane,
  ManualStoredFeature,
} from '@/services/manualCad';

interface ManualCadFeatureFormProps {
  type: ManualFeatureType;
  /** Existing sketches, for the Pad/Pocket profile reference (newest last). */
  sketches: ManualStoredFeature[];
  disabled?: boolean;
  onSubmit: (input: ManualFeatureInput) => void;
}

// A small numeric field. Keeps its own string state so a half-typed value
// doesn't fight React; the parent reads the parsed number on submit.
function NumField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const id = `mc-${label.replace(/[^a-z]+/gi, '-').toLowerCase()}`;
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-xs text-telekinesis-text-secondary">
        {label}
      </Label>
      <Input
        id={id}
        type="number"
        inputMode="decimal"
        min={0}
        step="any"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-8 text-xs"
      />
    </div>
  );
}

const PLANES: ManualPlane[] = ['XY', 'XZ', 'YZ'];
const pos = (s: string) => {
  const n = parseFloat(s);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * Parameter panel for one Manual-CAD feature. Renders the fields for the given
 * type, validates (positive finite numbers, a resolvable sketch ref), and emits
 * a typed ManualFeatureInput. Backend re-validates against the whitelisted
 * schema, so this is a UX nicety, not the security boundary.
 */
export function ManualCadFeatureForm({
  type,
  sketches,
  disabled,
  onSubmit,
}: ManualCadFeatureFormProps) {
  const lastSketchId = sketches[sketches.length - 1]?.id ?? '';

  // Shared field state (only the relevant fields are read per type).
  const [plane, setPlane] = useState<ManualPlane>('XY');
  const [shape, setShape] = useState<'rectangle' | 'circle'>('rectangle');
  const [width, setWidth] = useState('20');
  const [height, setHeight] = useState('10');
  const [radius, setRadius] = useState('5');
  const [length, setLength] = useState('10');
  const [depth, setDepth] = useState('5');
  const [filletR, setFilletR] = useState('1');
  const [chamferD, setChamferD] = useState('1');
  const [sketchId, setSketchId] = useState(lastSketchId);
  const [err, setErr] = useState<string | null>(null);

  const sketchLabel = (s: ManualStoredFeature, i: number) => {
    const kind = (s as { profile?: { kind?: string } }).profile?.kind ?? '…';
    return `Sketch ${i + 1} (${kind})`;
  };

  const build = (): ManualFeatureInput | string => {
    switch (type) {
      case 'Sketch': {
        if (shape === 'rectangle') {
          const w = pos(width),
            h = pos(height);
          if (!w || !h) return 'Width and height must be positive.';
          return {
            type: 'Sketch',
            plane,
            profile: { kind: 'rectangle', width: w, height: h },
          };
        }
        const r = pos(radius);
        if (!r) return 'Radius must be positive.';
        return {
          type: 'Sketch',
          plane,
          profile: { kind: 'circle', radius: r },
        };
      }
      case 'Pad': {
        if (!sketchId) return 'Pick a sketch to pad.';
        const l = pos(length);
        if (!l) return 'Length must be positive.';
        return { type: 'Pad', sketchId, length: l };
      }
      case 'Pocket': {
        if (!sketchId) return 'Pick a sketch to pocket.';
        const d = pos(depth);
        if (!d) return 'Depth must be positive.';
        return { type: 'Pocket', sketchId, depth: d };
      }
      case 'Fillet': {
        const r = pos(filletR);
        if (!r) return 'Radius must be positive.';
        return { type: 'Fillet', radius: r };
      }
      case 'Chamfer': {
        const d = pos(chamferD);
        if (!d) return 'Distance must be positive.';
        return { type: 'Chamfer', distance: d };
      }
      case 'Boolean':
        return 'Boolean is not available yet.';
    }
  };

  const submit = () => {
    const res = build();
    if (typeof res === 'string') {
      setErr(res);
      return;
    }
    setErr(null);
    onSubmit(res);
  };

  return (
    <div className="w-56 space-y-3">
      <p className="text-sm font-medium text-telekinesis-neutral-50">{type}</p>

      {type === 'Sketch' && (
        <>
          <div className="space-y-1">
            <Label className="text-xs text-telekinesis-text-secondary">
              Plane
            </Label>
            <Select
              value={plane}
              onValueChange={(v) => setPlane(v as ManualPlane)}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PLANES.map((p) => (
                  <SelectItem key={p} value={p} className="text-xs">
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-telekinesis-text-secondary">
              Shape
            </Label>
            <Select
              value={shape}
              onValueChange={(v) => setShape(v as 'rectangle' | 'circle')}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="rectangle" className="text-xs">
                  Rectangle
                </SelectItem>
                <SelectItem value="circle" className="text-xs">
                  Circle
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          {shape === 'rectangle' ? (
            <div className="grid grid-cols-2 gap-2">
              <NumField label="Width" value={width} onChange={setWidth} />
              <NumField label="Height" value={height} onChange={setHeight} />
            </div>
          ) : (
            <NumField label="Radius" value={radius} onChange={setRadius} />
          )}
        </>
      )}

      {(type === 'Pad' || type === 'Pocket') && (
        <>
          <div className="space-y-1">
            <Label className="text-xs text-telekinesis-text-secondary">
              Sketch
            </Label>
            <Select value={sketchId} onValueChange={setSketchId}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Pick a sketch" />
              </SelectTrigger>
              <SelectContent>
                {sketches.map((s, i) => (
                  <SelectItem key={s.id} value={s.id} className="text-xs">
                    {sketchLabel(s, i)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {type === 'Pad' ? (
            <NumField label="Length (mm)" value={length} onChange={setLength} />
          ) : (
            <NumField label="Depth (mm)" value={depth} onChange={setDepth} />
          )}
        </>
      )}

      {type === 'Fillet' && (
        <NumField
          label="Radius (mm) — all edges"
          value={filletR}
          onChange={setFilletR}
        />
      )}
      {type === 'Chamfer' && (
        <NumField
          label="Distance (mm) — all edges"
          value={chamferD}
          onChange={setChamferD}
        />
      )}

      {err && <p className="text-xs text-red-400">{err}</p>}

      <Button
        type="button"
        size="sm"
        disabled={disabled}
        onClick={submit}
        className="w-full bg-telekinesis-gold text-black hover:bg-telekinesis-gold/80"
      >
        Add {type}
      </Button>
    </div>
  );
}
